import type {
    AgentListItem,
    AgentTeam,
    PermissionGroup,
} from "@albertine/types/openapi/lmt";
import { AgentAvatar } from "@prototypes/agents/AgentAvatar";
import { DataTable, SortableHeader } from "@prototypes/data-table/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { AgentTeamSelect, agentTeamLabel } from "./AgentTeamSelect";

const AGENT_STATUSES = ["Active", "Inactive"] as const;
type AgentStatus = (typeof AGENT_STATUSES)[number];

type Props = {
    agents: AgentListItem[];
    teams: AgentTeam[];
    permissionGroups: PermissionGroup[];
    emptyMessage: string;
    canUpdateAgent: (agent: AgentListItem) => boolean;
    canAssignTeam: (agent: AgentListItem) => boolean;
    canEditAgent: (agent: AgentListItem) => boolean;
    onEditAgent: (agent: AgentListItem) => void;
    onAssignTeam: (agentId: string, teamId: string | null) => void;
    onUpdatePermissionGroup: (agentId: string, permissionGroup: string) => void;
    onRequestStatusChange: (agent: AgentListItem, nextStatus: AgentStatus) => void;
};

export function AgentsTable({
    agents,
    teams,
    permissionGroups,
    emptyMessage,
    canUpdateAgent,
    canAssignTeam,
    canEditAgent,
    onEditAgent,
    onAssignTeam,
    onUpdatePermissionGroup,
    onRequestStatusChange,
}: Props) {
    const permissionLabels: Record<string, string> = {};
    for (const group of permissionGroups) {
        permissionLabels[group.id] = group.name;
    }

    const columns = buildColumns({
        teams,
        permissionGroups,
        permissionLabels,
        canUpdateAgent,
        canAssignTeam,
        canEditAgent,
        onEditAgent,
        onAssignTeam,
        onUpdatePermissionGroup,
        onRequestStatusChange,
    });

    return (
        <DataTable
            data={agents}
            columns={columns}
            getRowId={(agent) => agent.id}
            emptyMessage={emptyMessage}
            initialSorting={[{ id: "name", desc: false }]}
        />
    );
}

type ColumnDeps = {
    teams: AgentTeam[];
    permissionGroups: PermissionGroup[];
    permissionLabels: Record<string, string>;
    canUpdateAgent: (agent: AgentListItem) => boolean;
    canAssignTeam: (agent: AgentListItem) => boolean;
    canEditAgent: (agent: AgentListItem) => boolean;
    onEditAgent: (agent: AgentListItem) => void;
    onAssignTeam: (agentId: string, teamId: string | null) => void;
    onUpdatePermissionGroup: (agentId: string, permissionGroup: string) => void;
    onRequestStatusChange: (agent: AgentListItem, nextStatus: AgentStatus) => void;
};

function buildColumns({
    teams,
    permissionGroups,
    permissionLabels,
    canUpdateAgent,
    canAssignTeam,
    canEditAgent,
    onEditAgent,
    onAssignTeam,
    onUpdatePermissionGroup,
    onRequestStatusChange,
}: ColumnDeps): ColumnDef<AgentListItem, unknown>[] {
    return [
        {
            id: "name",
            accessorFn: (agent) => agent.name.trim() || undefined,
            header: ({ column }) => (
                <SortableHeader
                    label="Full Name"
                    sorted={column.getIsSorted()}
                    column={column}
                />
            ),
            cell: ({ row }) => {
                const agent = row.original;
                const nameContent = (
                    <>
                        <AgentAvatar name={agent.name} avatarUrl={null} />
                        <span className="truncate text-sm text-zinc-900 leading-body-sm">
                            {agent.name}
                        </span>
                    </>
                );

                if (!canEditAgent(agent)) {
                    return (
                        <div
                            className="flex min-w-0 items-center gap-2 px-4 py-2"
                            title={agent.name}
                        >
                            {nameContent}
                        </div>
                    );
                }

                return (
                    <Button
                        variant="ghost"
                        className="h-auto w-full min-w-0 justify-start gap-2 rounded-none px-4 py-2 font-normal hover:bg-zinc-100"
                        title={agent.name}
                        aria-label={`Edit ${agent.name}`}
                        onClick={() => onEditAgent(agent)}
                    >
                        {nameContent}
                    </Button>
                );
            },
            sortUndefined: "last",
            meta: {
                headerClassName: "w-[22%]",
                cellClassName: "w-[22%] overflow-hidden p-0",
            },
        },
        {
            id: "email",
            accessorFn: (agent) => agent.email.trim() || undefined,
            header: ({ column }) => (
                <SortableHeader
                    label="Email"
                    sorted={column.getIsSorted()}
                    column={column}
                />
            ),
            cell: ({ row }) => (
                <span
                    className="block w-full truncate text-xs text-zinc-500 leading-4"
                    title={row.original.email}
                >
                    {row.original.email}
                </span>
            ),
            sortUndefined: "last",
            meta: {
                headerClassName: "hidden w-[20%] min-[1360px]:table-cell",
                cellClassName:
                    "hidden w-[20%] overflow-hidden px-4 min-[1360px]:table-cell",
            },
        },
        {
            id: "team",
            accessorFn: (agent) =>
                agent.teamId
                    ? (teams.find((team) => team.id === agent.teamId)?.name ??
                      undefined)
                    : undefined,
            header: ({ column }) => (
                <SortableHeader
                    label="Team"
                    sorted={column.getIsSorted()}
                    column={column}
                />
            ),
            cell: ({ row }) => {
                const agent = row.original;

                if (!canAssignTeam(agent)) {
                    return (
                        <span className="block truncate text-sm text-zinc-500 leading-body-sm">
                            {agentTeamLabel(teams, agent.teamId)}
                        </span>
                    );
                }

                return (
                    <AgentTeamSelect
                        teams={teams}
                        value={agent.teamId}
                        size="sm"
                        aria-label={`Team for ${agent.name}`}
                        onValueChange={(teamId) => onAssignTeam(agent.id, teamId)}
                    />
                );
            },
            sortUndefined: "last",
            meta: {
                headerClassName: "w-[16%]",
                cellClassName: "w-[16%] px-4",
            },
        },
        {
            id: "permissions",
            accessorFn: (agent) =>
                agent.permissionGroup
                    ? (permissionLabels[agent.permissionGroup] ?? agent.permissionGroup)
                    : undefined,
            header: ({ column }) => (
                <SortableHeader
                    label="Permissions"
                    sorted={column.getIsSorted()}
                    column={column}
                />
            ),
            cell: ({ row }) => {
                const agent = row.original;
                const label = agent.permissionGroup
                    ? (permissionLabels[agent.permissionGroup] ?? agent.permissionGroup)
                    : "";

                if (!canUpdateAgent(agent)) {
                    return (
                        <span className="block truncate text-sm text-zinc-500 leading-body-sm">
                            {label}
                        </span>
                    );
                }

                return (
                    <Select
                        items={permissionLabels}
                        value={agent.permissionGroup ?? undefined}
                        onValueChange={(value) => {
                            if (value) {
                                onUpdatePermissionGroup(agent.id, value as string);
                            }
                        }}
                    >
                        <SelectTrigger
                            size="sm"
                            className="w-full"
                            aria-label={`Permissions for ${agent.name}`}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {permissionGroups
                                .toSorted((lhs, rhs) =>
                                    lhs.name.localeCompare(rhs.name),
                                )
                                .map((group) => (
                                    <SelectItem key={group.id} value={group.id}>
                                        {group.name}
                                    </SelectItem>
                                ))}
                        </SelectContent>
                    </Select>
                );
            },
            sortUndefined: "last",
            meta: {
                headerClassName: "w-[14%]",
                cellClassName: "w-[14%] px-4",
            },
        },
        {
            id: "status",
            accessorFn: (agent) => (agent.disabled ? "Inactive" : "Active"),
            header: ({ column }) => (
                <SortableHeader
                    label="Status"
                    sorted={column.getIsSorted()}
                    column={column}
                />
            ),
            cell: ({ row }) => {
                const agent = row.original;
                const status: AgentStatus = agent.disabled ? "Inactive" : "Active";

                if (!canUpdateAgent(agent)) {
                    return (
                        <span className="block truncate text-sm text-zinc-500 leading-body-sm">
                            <StatusLabel status={status} />
                        </span>
                    );
                }

                return (
                    <Select
                        value={status}
                        onValueChange={(value) =>
                            onRequestStatusChange(agent, value as AgentStatus)
                        }
                    >
                        <SelectTrigger
                            size="sm"
                            className="w-full"
                            aria-label={`Status for ${agent.name}`}
                        >
                            <SelectValue>
                                {(value) => (
                                    <StatusLabel status={value as AgentStatus} />
                                )}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {AGENT_STATUSES.map((entry) => (
                                <SelectItem key={entry} value={entry}>
                                    <StatusLabel status={entry} />
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );
            },
            meta: {
                headerClassName: "w-[12%]",
                cellClassName: "w-[12%] px-4",
            },
        },
        {
            id: "createdAt",
            accessorFn: (agent) => agent.createdAt || undefined,
            header: ({ column }) => (
                <SortableHeader
                    label="Created On"
                    sorted={column.getIsSorted()}
                    column={column}
                />
            ),
            cell: ({ row }) => (
                <span className="block truncate text-sm text-zinc-900 leading-body-sm">
                    {formatCreatedOn(row.original.createdAt)}
                </span>
            ),
            sortUndefined: "last",
            meta: {
                headerClassName: "hidden w-[14%] min-[1360px]:table-cell",
                cellClassName: "hidden w-[14%] px-4 min-[1360px]:table-cell",
            },
        },
    ];
}

function formatCreatedOn(createdAt: string | null | undefined): string {
    if (!createdAt) return "n/a";
    return format(parseISO(createdAt), "d MMM yyyy");
}

function StatusLabel({ status }: { status: AgentStatus }) {
    return (
        <span className="flex items-center gap-2">
            {status === "Active" ? (
                <span
                    aria-hidden
                    className="size-1.5 shrink-0 rounded-full bg-emerald-500"
                />
            ) : null}
            {status}
        </span>
    );
}

export type { AgentStatus };
